import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {
    EMPTY,
    Subject,
    catchError,
    finalize,
    takeUntil
} from 'rxjs';

import {PromoMxObj} from '../../../../../../generated/generated.model';
import {ImageDisplay, MxCarouselModule, MxCreativeTypeService, MxThumbnailModule} from 'mx-core';
import {PrlZonePreviewDialogComponent} from '../prl-zone-preview-dialog/prl-zone-preview-dialog.component';
import {PrlZoneDataService} from '../../../../shared/services/prl-zone-data-service';

@Component({
    selector: 'prl-zone-view',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MxThumbnailModule,
        MxCarouselModule
    ],
    templateUrl: './prl-zone-view.component.html',
    styleUrls: ['./prl-zone-view.component.scss'],
})
export class PrlZoneViewComponent implements OnChanges, OnDestroy {

    @Input({required: true}) promos: PromoMxObj[] = [];

    images: ImageDisplay[] = [];
    selectedIndex = 0;
    previewLoading = false;

    private destroy$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private zoneDataService: PrlZoneDataService
    ) {
    }

    get hasMultiplePromos(): boolean {
        return (this.promos?.length ?? 0) > 1;
    }

    get selectedPromo(): PromoMxObj {
        return this.promos?.[this.selectedIndex] ?? this.promos?.[0];
    }

    getThumbnailClass(promo?: PromoMxObj): string {
        return MxCreativeTypeService.getDefaultThumbnailClass(promo?.typeName);
    }

    getThumbnailUrl(promo?: PromoMxObj): string | undefined {
        return promo?.id ? this.zoneDataService.getThumbnailUrl(promo.id) : undefined;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.promos) {
            this.selectedIndex = 0;
            this.images = (this.promos ?? [])
                .map(promo => this.getThumbnailUrl(promo))
                .filter((url): url is string => !!url)
                .map(url => new ImageDisplay(url));
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onImageChange(index: number): void {
        this.selectedIndex = index;
    }

    openPreview(): void {
        const promo = this.selectedPromo;

        if (!promo?.id || this.previewLoading) {
            return;
        }

        this.previewLoading = true;

        this.zoneDataService.getPromoPreview(promo.id)
            .pipe(
                takeUntil(this.destroy$),
                catchError(() => {
                    this.openPreviewDialog(promo);
                    return EMPTY;
                }),
                finalize(() => {
                    this.previewLoading = false;
                })
            )
            .subscribe(res => {
                if (res.status === 200 && res.body) {
                    const previewUrl = URL.createObjectURL(res.body);
                    const dialogRef = this.openPreviewDialog(promo, previewUrl);

                    dialogRef.afterClosed().subscribe(() => URL.revokeObjectURL(previewUrl));
                    return;
                }

                this.openPreviewDialog(promo);
            });
    }

    private openPreviewDialog(promo: PromoMxObj, previewUrl?: string) {
        return this.dialog.open(PrlZonePreviewDialogComponent, {
            data: {
                title: promo.name,
                previewUrl,
                typeName: promo.typeName,
                defaultThumbnailClass: this.getThumbnailClass(promo),
                loading: false
            },
            width: '900px',
            maxWidth: '95vw',
            panelClass: 'prl-zone-preview-dialog'
        });
    }
}